import Button from '@mui/material/Button'
import { styled, useTheme } from '@mui/material/styles'
import React, { useRef, useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useSpring, animated } from 'react-spring'
import Step from '../../constants/Step'
import { OrderProduct } from '../../entities/order-product/OrderProduct'
import ProductControlSwitcher from '../product-control-switcher/ProductControlSwitcher'
import ProductDeclineButton from '../product-decline-button/ProductDeclineButton'
import { useGlobalLoading } from '../../contexts/GlobalLoadingContext'
import messages from './ProductItemControls.messages'

export interface IOwnProps {
  product: OrderProduct
}

export interface IStateProps {
  notPreparedOrderId?: number
  acceptLoading?: boolean
  printLoading?: boolean
  anyProductLoading?: boolean
}

export interface IDispatchProps {
  requestProductAccept: (product: OrderProduct, onEnd?: () => void) => void
  requestPrint: (product: OrderProduct, onEnd?: () => void) => void
  resetFailedOrderId: () => void
  setProductLoading: (productKey: string, isLoading: boolean) => void
}

type IProps = IOwnProps & IStateProps & IDispatchProps

const PrintButton = animated(styled(Button)({
  width: 80,
}))

const ProductItemControls: React.FC<IProps> = (props) => {
  const {
    product,
    notPreparedOrderId,
    acceptLoading = false,
    printLoading = false,
    anyProductLoading = false,
    requestProductAccept,
    requestPrint,
    resetFailedOrderId,
    setProductLoading,
  } = props

  const { showLoading, hideLoading } = useGlobalLoading()

  // Local state for immediate UI feedback
  const [localAcceptLoading, setLocalAcceptLoading] = useState(false)
  const [localPrintLoading, setLocalPrintLoading] = useState(false)

  // Combine Redux and local loading states
  const isAcceptLoading = acceptLoading || localAcceptLoading
  const isPrintLoading = printLoading || localPrintLoading


  const shouldHighlightPrintButton = notPreparedOrderId === product.order?.orderId && product.order?.isInProgress()

  const [highlightedPrintButtonStyles] = useSpring({
    x: shouldHighlightPrintButton ? 1 : 0,
    config: {
      duration: 400,
    },
    onResolve: () => shouldHighlightPrintButton && resetFailedOrderId(),
  }, [notPreparedOrderId])
  const theme = useTheme()

  // Ref to track if component is mounted
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  // Track previous loading states to only clear on actual transitions
  const prevAcceptLoading = useRef(acceptLoading)
  const prevPrintLoading = useRef(printLoading)

  // Effect to clear local loading when Redux loading transitions from true to false
  useEffect(() => {
    if (prevAcceptLoading.current === true && acceptLoading === false) {
      setLocalAcceptLoading(false)
    }
    prevAcceptLoading.current = acceptLoading
  }, [acceptLoading, product.id])

  useEffect(() => {
    if (prevPrintLoading.current === true && printLoading === false) {
      setLocalPrintLoading(false)
    }
    prevPrintLoading.current = printLoading
  }, [printLoading, product.id])

  const handleAcceptClick = () => {
    showLoading()
    setTimeout(() => hideLoading(), 2000)
    requestProductAccept(product)
  }

  const handlePrintClick = () => {
    showLoading()
    setTimeout(() => hideLoading(), 2000)
    requestPrint(product)
  }

  if (product.canPrint()) {
    const isAwaitingFinalPo = !product.order?.hasFinalPo()

    if (isAwaitingFinalPo) {
      return (
        <PrintButton
          disabled
          variant='contained'
          color='primary'
        >
          <FormattedMessage {...messages.print} />
        </PrintButton>
      )
    }

    return (
      <PrintButton
        onClick={handlePrintClick}
        disabled={anyProductLoading}
        variant='contained'
        color='primary'
        style={{
          transform: highlightedPrintButtonStyles.x
            .to({
              range: [0, 0.30, 0.65, 1],
              output: [0, 14, 10, 12],
            })
            .to(x => `translateX(${x}px)`),
          color: highlightedPrintButtonStyles.x
            .to({
              range: [0, 0.05, 1],
              output: [
                theme.palette.text.secondary,
                theme.palette.error.main,
                theme.palette.error.main,
              ],
            }),
        }}
      >
        <FormattedMessage {...messages.print} />
      </PrintButton>
    )
  }


  return (
    <>
      {
        product.order?.cycle?.column?.step === Step.INCOMING
        &&
        <ProductControlSwitcher
          initialContent={() => (
            product.canMoveToInProgress() && !product.isDeclined
            &&
            <Button
              onClick={handleAcceptClick}
              disabled={anyProductLoading}
              variant='contained'
              color='primary'
            >
              <FormattedMessage {...messages.accept} />
            </Button>
          )}
          toggleContent={() => (
            <>
              {product.canMoveToInProgress() && product.isDeclined
                &&
                <Button
                  onClick={handleAcceptClick}
                  disabled={anyProductLoading}
                  variant='contained'
                  color='secondary'
                >
                  <FormattedMessage {...messages.accept} />
                </Button>
              }

              {product.canDecline()
                && <ProductDeclineButton product={product} />
              }
            </>
          )}
        />
      }
    </>
  )
}

export default ProductItemControls
